export interface DailyChallengeData {
    date: string;
    seed: number;
    playerBest: {
        height: number;
        strikes: number;
        completed: boolean;
    } | null;
    globalBest: {
        height: number;
        strikes: number;
        playerName: string;
    } | null;
}

export interface DailyChallengeProgress {
    height: number;
    strikes: number;
    completed: boolean;
}

export class DailyChallenge {
    private static readonly STORAGE_KEY = 'golfUP_dailyChallenge';
    private static readonly SEED_BASE = 123456789; // Fixed base for deterministic generation

    /**
     * Generate a deterministic seed based on date
     * This ensures the same "impossible" level every day
     */
    public static generateDailySeed(date: Date = new Date()): number {
        const dateString = date.toDateString();
        let hash = 0;

        // Simple hash function for date string
        for (let i = 0; i < dateString.length; i++) {
            const char = dateString.charCodeAt(i);
            hash = ((hash << 5) - hash) + char;
            hash = hash & hash; // Convert to 32-bit integer
        }

        // Make it "impossible" by adding complexity factors
        const impossibleSeed = Math.abs(hash) + this.SEED_BASE;
        return impossibleSeed % 1000000; // Keep within reasonable range
    }

    /**
     * Get today's challenge data
     */
    public static getTodayChallenge(): DailyChallengeData {
        const today = new Date();
        const dateString = today.toISOString().split('T')[0]; // YYYY-MM-DD format

        const challenge: DailyChallengeData = {
            date: dateString,
            seed: this.generateDailySeed(today),
            playerBest: null,
            globalBest: null
        };

        // Load stored progress
        const stored = localStorage.getItem(this.STORAGE_KEY);
        if (stored) {
            try {
                const allProgress = JSON.parse(stored);
                const todayProgress = allProgress[dateString];
                if (todayProgress) {
                    challenge.playerBest = {
                        height: todayProgress.height || 0,
                        strikes: todayProgress.strikes || 0,
                        completed: todayProgress.completed || false
                    };
                }
            } catch (e) {
                console.warn('Failed to parse daily challenge progress:', e);
            }
        }

        return challenge;
    }

    /**
     * Save player progress for today's challenge
     */
    public static saveProgress(progress: DailyChallengeProgress): void {
        const today = new Date().toISOString().split('T')[0];

        const stored = localStorage.getItem(this.STORAGE_KEY);
        let allProgress = {};

        if (stored) {
            try {
                allProgress = JSON.parse(stored);
            } catch (e) {
                console.warn('Failed to parse stored progress, resetting');
            }
        }

        allProgress[today] = {
            height: Math.max(allProgress[today]?.height || 0, progress.height),
            strikes: progress.strikes, // Keep the lowest strikes count
            completed: progress.completed || allProgress[today]?.completed || false,
            lastUpdated: Date.now()
        };

        localStorage.setItem(this.STORAGE_KEY, JSON.stringify(allProgress));
    }

    /**
     * Get streak count (consecutive days with completion)
     */
    public static getStreakCount(): number {
        const stored = localStorage.getItem(this.STORAGE_KEY);
        if (!stored) return 0;

        try {
            const allProgress = JSON.parse(stored);
            let streak = 0;
            let checkDate = new Date();

            // Check last 30 days for streak
            for (let i = 0; i < 30; i++) {
                const dateString = checkDate.toISOString().split('T')[0];
                const dayProgress = allProgress[dateString];

                if (dayProgress?.completed) {
                    streak++;
                } else {
                    break; // Streak broken
                }

                checkDate.setDate(checkDate.getDate() - 1);
            }

            return streak;
        } catch (e) {
            console.warn('Failed to calculate streak:', e);
            return 0;
        }
    }

    /**
     * Generate shareable text for progress
     */
    public static generateShareText(progress: DailyChallengeProgress, streak: number): string {
        const today = new Date();
        const dayName = today.toLocaleDateString('en-US', { weekday: 'long' });
        const dateStr = today.toLocaleDateString('en-US', {
            month: 'short',
            day: 'numeric',
            year: 'numeric'
        });

        let text = `🎯 golfUP Daily Challenge - ${dayName}, ${dateStr}\n`;
        text += `🏔️ Reached: ${progress.height.toFixed(0)}ft\n`;
        text += `⛳ Strikes: ${progress.strikes}\n`;

        if (progress.completed) {
            text += `✅ COMPLETED!\n`;
        }

        if (streak > 1) {
            text += `🔥 Streak: ${streak} days\n`;
        }

        text += `\nCan you beat my score? #golfUP`;

        return text;
    }

    /**
     * Check if challenge is completed
     */
    public static isCompleted(height: number): boolean {
        // Consider completed if they reach a reasonable height threshold
        // This could be adjusted based on difficulty
        return height >= 500; // 500ft seems like a good completion threshold
    }

    /**
     * Get difficulty rating based on completion rate (for future use)
     */
    public static getDifficultyRating(): 'Easy' | 'Medium' | 'Hard' | 'Impossible' {
        // This could be based on global completion rates in the future
        return 'Hard'; // Default to hard for now since it's "impossible"
    }
}



